import React, { useMemo } from 'react';
import { formatCurrency } from '@/lib/portfolioCalculations';

interface InvestmentsSummaryHeaderProps {
  accounts: Array<{ id: string; currency?: string; balance?: number }>;
  positionsCount: number;
  defaultCurrency?: string; // formatting only; no FX
}

export const InvestmentsSummaryHeader: React.FC<InvestmentsSummaryHeaderProps> = ({
  accounts,
  positionsCount,
  defaultCurrency = 'CAD',
}) => {
  const summary = useMemo(() => {
    const allAccounts = accounts || [];

    // Assets total: sum of API balances only
    const totalAssets = allAccounts.reduce((sum, a) => sum + (a.balance || 0), 0);

    // Currencies from API balances
    const byCurrency: Record<string, number> = {};
    allAccounts.forEach(a => {
      const ccy = (a.currency || 'CAD').toUpperCase();
      byCurrency[ccy] = (byCurrency[ccy] || 0) + (a.balance || 0);
    });

    const defaultCcy = (defaultCurrency || 'CAD').toUpperCase();
    const entries = Object.entries(byCurrency);
    entries.sort((a, b) => {
      if (a[0] === defaultCcy && b[0] !== defaultCcy) return -1;
      if (b[0] === defaultCcy && a[0] !== defaultCcy) return 1;
      return b[1] - a[1];
    });
    const top2 = entries.slice(0, 2);

    return { totalAssets, top2, accountsCount: allAccounts.length };
  }, [accounts, defaultCurrency]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Assets total */}
      <div className="bg-card border rounded-lg p-4 shadow-sm min-w-0">
        <p className="text-sm font-medium text-muted-foreground mb-2">ASSETS</p>
        <div className="text-2xl font-bold tabular-nums text-green-600 whitespace-nowrap truncate">
          {formatCurrency(summary.totalAssets, (defaultCurrency || 'CAD'), { showDecimals: true })}
        </div>
      </div>

      {/* Currencies (bottom-aligned) */}
      <div className="bg-card border rounded-lg p-4 shadow-sm min-w-0">
        <div className="h-full flex flex-col justify-end gap-1">
          {summary.top2.map(([ccy, value]) => (
            <div key={ccy} className="text-sm flex items-baseline gap-2 min-w-0">
              <span className="w-10 text-muted-foreground font-mono">{ccy}</span>
              <span className="font-medium tabular-nums flex-1 min-w-0 text-right truncate">{formatCurrency(value, ccy as any, { showDecimals: true, locale: (ccy === 'CAD' ? 'en-CA' : ccy === 'USD' ? 'en-US' : ccy === 'JPY' ? 'ja-JP' : undefined) })}</span>
            </div>
          ))}
          {summary.top2.length === 0 && (
            <div className="text-sm text-muted-foreground">No balances</div>
          )}
        </div>
      </div>

      {/* Sources */}
      <div className="bg-card border rounded-lg p-4 shadow-sm min-w-0">
        <p className="text-sm font-medium text-muted-foreground mb-2">SOURCES</p>
        <div className="text-sm">
          <div><span className="font-semibold">{summary.accountsCount}</span> Accounts</div>
          <div><span className="font-semibold">{positionsCount}</span> Holdings</div>
        </div>
      </div>
    </div>
  );
};

export default InvestmentsSummaryHeader;
